import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { TNil } from '@app/app.interface';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { ErrorFormatterService } from '@core/services/error-formatter.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { ComponentStore } from '@ngrx/component-store';
import { TPlatform } from '@shared/components/social-badge-list/social-badge-list.interface';
import { HttpUtilitiesService, IGenericCrudRequestConstructionParameters } from '@utilities/http-utilities.service';
import { EMPTY, MonoTypeOperatorFunction, Observable, pipe, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { IChannel, ICreator, ICreatorUpdate, ICreatorWithChannelCreate, TCreatorApi } from './creators.interface';
import { CreatorsService } from './creators.service';

const CREATOR_NAME_ALREADY_EXISTS_CODE = 'creator_name_already_exists' as const;

interface ICreatorsState {
  creator: ICreator | TNil;
  creators: ICreator[];
  creatorsLoading: boolean;
  error: string | null;
  loading: boolean;
  total: number;
}

const initialState: ICreatorsState = {
  creator: null,
  creators: [],
  creatorsLoading: false,
  error: null,
  loading: false,
  total: 0,
};

@Injectable()
export class CreatorsStore extends ComponentStore<ICreatorsState> {
  public readonly creators$ = this.selectSignal((state: ICreatorsState) => state.creators);
  public readonly creatorsLoading$ = this.selectSignal((state: ICreatorsState) => state.creatorsLoading);
  public readonly loading$ = this.selectSignal((state: ICreatorsState) => state.loading);
  public readonly error$ = this.selectSignal((state: ICreatorsState) => state.error);
  public readonly total$ = this.selectSignal((state: ICreatorsState) => state.total);

  public readonly newCreator$ = signal<ICreator | null>(null);
  public readonly updatedCreator$ = signal<ICreator | null>(null);
  public readonly creator$ = signal<ICreator | null>(null);
  public readonly nameError$ = signal<string | null>(null);

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError((error: HttpErrorResponse) => {
        const nameErrorDetail = this.errorFormatterService
          .getErrorDetails(error.error)
          .find((detail) => detail.code === CREATOR_NAME_ALREADY_EXISTS_CODE);

        if (nameErrorDetail) {
          const { message } = this.errorFormatterService.getErrorMessage(nameErrorDetail, error.error);
          this.snackbarService.openError(message);
        }

        this.patchState({
          creatorsLoading: false,
          error: errorMessage,
          loading: false,
        });

        return EMPTY;
      }),
    );

  public readonly fetchCreators = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ creatorsLoading: true, error: null, loading: true })),
      switchMap((params: IGenericCrudRequestConstructionParameters) =>
        this.creatorsService.getCreators(this.httpUtilitiesService.insertGenericCrudRequestParameters(params)).pipe(
          tap((creators: IApiResponse<TCreatorApi[]>) => {
            const mappedCreators: ICreator[] = (creators?.data ?? []).map((creator) => ({
              ...creator,
              channels: creator.channels.map((channel) => ({
                ...channel,
                platform: channel.platform as TPlatform,
                platformCreatorId: channel.platformCreatorId,
              })) as IChannel[],
            }));

            this.patchState({
              creators: mappedCreators,
              creatorsLoading: false,
              loading: false,
              total: creators?.meta?.total ?? 0,
            });
          }),
          this.handleError('Failed to load creators'),
        ),
      ),
    );
  });

  public readonly fetchCreator = this.effect(
    (
      params$: Observable<{
        creatorId?: string;
        adminMode?: boolean;
        creatorMode?: boolean;
      }>,
    ) => {
      return params$.pipe(
        tap(() => this.patchState({ error: null, loading: true })),
        switchMap(({ creatorId, adminMode, creatorMode }) => {
          const params = new HttpParams().set('joins', 'channels,user_invitation');

          const fetch$ = adminMode
            ? this.creatorsService.getAdminCreator(creatorId!, params)
            : creatorMode
              ? this.creatorsService.getCurrentCreator(params)
              : this.creatorsService.getCreator(creatorId!, params);

          return fetch$.pipe(
            tap((creator: ICreator | undefined) => {
              this.creator$.set(creator ?? null);
              this.patchState({ creator, loading: false });
            }),
            this.handleError('Failed to load creators'),
          );
        }),
      );
    },
  );

  public readonly deleteCreator = this.effect((creatorId$: Observable<string>) => {
    return creatorId$.pipe(
      tap(() => this.patchState({ error: null, loading: true })),
      switchMap((id) =>
        this.creatorsService.deleteCreator(id).pipe(
          tap(() => {
            this.patchState({ creators: this.get().creators.filter((creator) => creator.id !== id), loading: false });
          }),
          this.handleError('Failed to delete creator'),
        ),
      ),
    );
  });

  public readonly createCreator = this.effect((creator$: Observable<ICreatorWithChannelCreate>) => {
    return creator$.pipe(
      tap(() => this.patchState({ error: null, loading: true })),
      switchMap((creator) =>
        this.creatorsService.createCreator(creator).pipe(
          tap((createdCreator) => {
            this.patchState({
              creators: [...this.get().creators, createdCreator],
              loading: false,
            });
            this.newCreator$.set(createdCreator);
          }),
          this.handleError('Failed to create creator'),
        ),
      ),
    );
  });

  public readonly updateCreator = this.effect((creator$: Observable<{ id: string; creator: ICreatorUpdate }>) => {
    return creator$.pipe(
      tap(() => this.patchState({ error: null, loading: true })),
      switchMap(({ id, creator }) =>
        this.creatorsService.updateCreator(id, creator).pipe(
          tap((updatedCreator) => {
            this.patchState({
              creators: this.get().creators.map((c) => (c.id === id ? updatedCreator : c)),
              loading: false,
            });
            this.updatedCreator$.set(updatedCreator);
          }),
          this.handleError('Failed to update creator'),
        ),
      ),
    );
  });

  public updateCreatorName(id: string, creator: ICreatorUpdate): Observable<ICreator> {
    this.nameError$.set(null);

    return this.creatorsService.updateCreator(id, creator).pipe(
      tap((updatedCreator) => {
        this.patchState({
          creators: this.get().creators.map((c) => (c.id === id ? updatedCreator : c)),
        });
      }),
      catchError((error: HttpErrorResponse) => {
        const nameErrorDetail = this.errorFormatterService
          .getErrorDetails(error.error)
          .find((detail) => detail.code === CREATOR_NAME_ALREADY_EXISTS_CODE);

        if (nameErrorDetail) {
          this.nameError$.set(this.errorFormatterService.getErrorMessage(nameErrorDetail, error.error).message);
        }

        return throwError(() => error);
      }),
    );
  }

  constructor(
    private readonly creatorsService: CreatorsService,
    private readonly httpUtilitiesService: HttpUtilitiesService,
    private readonly errorFormatterService: ErrorFormatterService,
    private readonly snackbarService: SnackbarService,
  ) {
    super(initialState);
  }
}
